import { isUnauthorized, refusedAs } from '@crow/service-kit/web/api';
import { type AppBootstrap, appBootstrapSchema } from '@crowdex/agent-contracts/api';
import { apiFetch, apiResponse } from '@crowdex/host-kit/renderer/http';
import { type LocalWorkspace, listLocalWorkspaces } from '@/lib/local-conversations';
import { isLocalMachine, setServerProfile } from '@/lib/local-runtime';
import { unsubscribeFromPush } from '@/lib/push-notifications';

export class RuntimeServiceError extends Error {
  readonly issues: Readonly<Record<string, string>>;

  constructor(message: string, issues: Readonly<Record<string, string>> = {}) {
    super(message);
    this.name = 'RuntimeServiceError';
    this.issues = issues;
  }
}

export type RuntimeServices = {
  loadBootstrap(): Promise<AppBootstrap | null>;
  listWorkspaces(): Promise<LocalWorkspace[]>;
  /**
   * Where the browser goes to sign in with a Crowlingo account (Zitadel), coming back to
   * `callbackURL`. Only the cloud signs in this way: a server on this machine opens signed in.
   */
  signIn(callbackURL: string): Promise<string>;
  /** Signs out; the address that also ends the Crowlingo session, when there is one. */
  signOut(): Promise<string | null>;
};

async function webBootstrap(): Promise<AppBootstrap | null> {
  let data: unknown;
  try {
    data = await apiFetch<unknown>('/api/app/bootstrap');
  } catch (error) {
    // Signed out: the sign-in page.
    if (isUnauthorized(error)) return null;
    throw error;
  }
  const parsed = appBootstrapSchema.safeParse(data);
  if (!parsed.success) {
    throw new RuntimeServiceError('The server returned an invalid bootstrap response.');
  }
  // The local profile also exposes its workspace server; connect before the shell renders.
  await setServerProfile(parsed.data.profile);
  if (parsed.data.signInAgain === true && signInAgainOnce()) {
    // A service was added since the user signed in: Zitadel signs them in again without a page
    // while their session there lasts, then back here. Never twice in one tab (a refused one
    // shows the sign-in page).
    window.location.assign(silentSignIn(`${window.location.pathname}${window.location.search}`));
    return new Promise<never>(() => undefined);
  }
  return parsed.data;
}

const SILENT_SIGN_IN_KEY = 'crowdex:silent-sign-in';

/** True the first time in this tab: a silent sign-in that did not grant the audience is not retried. */
function signInAgainOnce(): boolean {
  try {
    if (window.sessionStorage.getItem(SILENT_SIGN_IN_KEY) !== null) return false;
    window.sessionStorage.setItem(SILENT_SIGN_IN_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/** The kit's sign-in start without a page (`prompt=none`), back to `next`. */
function silentSignIn(next: string): string {
  return `/api/app/auth/oidc/start?prompt=none&next=${encodeURIComponent(next)}`;
}

/** Checkouts on this machine (local profile) or Cloud workspaces (hosted). */
async function webWorkspaces(): Promise<LocalWorkspace[]> {
  if (isLocalMachine()) return listLocalWorkspaces();
  const data = await apiFetch<{
    workspaces?: Array<{
      id: string;
      logicalWorkspaceId: string;
      rootPath: string;
      name: string;
      updatedAt: string;
    }>;
  } | null>('/api/sync/bootstrap').catch(refusedAs(null));
  return (
    data?.workspaces?.map((workspace) => ({
      ...workspace,
      runtime: 'local' as const,
      sourceWorkspaceId: null,
      sourceRoot: null,
    })) ?? []
  );
}

/**
 * Where the browser goes to sign in through Crowlingo (the service kit's start, which goes on to
 * Zitadel), coming back to `next`. A server on this computer answers with its sign-in page's
 * `local_profile` error: it signs in from its launch link.
 */
async function signIn(next: string): Promise<string> {
  return `/api/app/auth/oidc/start?next=${encodeURIComponent(next)}`;
}

const webServices: RuntimeServices = {
  loadBootstrap: webBootstrap,
  listWorkspaces: webWorkspaces,
  signIn,
  async signOut() {
    // A device signed out stops showing the account's notifications (asked while signed in).
    await unsubscribeFromPush().catch(() => undefined);
    const response = await apiResponse('/api/auth/sign-out', { method: 'POST' }).catch(
      refusedAs(null),
    );
    const body = (await response?.json().catch(() => null)) as { url?: unknown } | null;
    return typeof body?.url === 'string' ? body.url : null;
  },
};

export function runtimeServices(): RuntimeServices {
  return webServices;
}
